import { useState, useEffect } from "react";
import { Users, Plus, RefreshCw, FileText, Clock, Play, CheckCircle, XCircle, LogIn, LogOut, Upload, Signature } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import axios from "axios";
import { FALLBACK_SMARTPAY_EMPLOYEES } from "@/data/dummyData";

export default function SmartPayHRIS() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("employees");
  const [employees, setEmployees] = useState([]);
  const [onboardingWorkflows, setOnboardingWorkflows] = useState([]);
  const [onboardingInstances, setOnboardingInstances] = useState([]);
  const [offboardings, setOffboardings] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [selectedEmpId, setSelectedEmpId] = useState("");

  const [showNewEmployee, setShowNewEmployee] = useState(false);
  const [showNewWorkflow, setShowNewWorkflow] = useState(false);
  const [showNewOffboarding, setShowNewOffboarding] = useState(false);
  const [showUploadDoc, setShowUploadDoc] = useState(false);
  const [showStartOnboarding, setShowStartOnboarding] = useState(false);
  const [clockedIn, setClockedIn] = useState(false);

  const [newEmployee, setNewEmployee] = useState({ full_name: "", email: "", phone: "", department: "", position: "", employee_type: "FULL_TIME", start_date: "" });
  const [newWorkflow, setNewWorkflow] = useState({ name: "", description: "", steps: "" });
  const [newOffboarding, setNewOffboarding] = useState({ employee_id: "", reason: "", checklist: "" });
  const [newDoc, setNewDoc] = useState({ employee_id: "", title: "", type: "CONTRACT", file_url: "" });
  const [onboardEmpId, setOnboardEmpId] = useState("");
  const [onboardWfId, setOnboardWfId] = useState("");

  const fetchEmployees = async () => { try { const res = await axios.get("/api/smartpay/employees"); const d = res.data.employees || []; setEmployees(d.length > 0 ? d : FALLBACK_SMARTPAY_EMPLOYEES); } catch { setEmployees(FALLBACK_SMARTPAY_EMPLOYEES); } };
  const fetchWorkflows = async () => { try { const res = await axios.get("/api/smartpay/onboarding-workflows"); setOnboardingWorkflows(res.data.workflows || []); } catch {} };
  const fetchOnboarding = async () => { try { const res = await axios.get("/api/smartpay/onboarding/status/all"); setOnboardingInstances(res.data.instances || []); } catch {} };
  const fetchOffboardings = async () => { try { const res = await axios.get("/api/smartpay/offboarding"); setOffboardings(res.data.offboardings || []); } catch {} };
  const fetchDocuments = async (empId) => {
    if (!empId) { setDocuments([]); return; }
    try { const res = await axios.get(`/api/smartpay/employees/${empId}/documents`); setDocuments(res.data.documents || []); } catch {}
  };
  const fetchTimeEntries = async (empId) => {
    if (!empId) { setTimeEntries([]); return; }
    try { const res = await axios.get(`/api/smartpay/time/entries/${empId}`); setTimeEntries(res.data.entries || []); } catch {}
  };

  useEffect(() => { fetchEmployees(); fetchWorkflows(); fetchOffboardings(); }, []);

  const handleCreateEmployee = async () => {
    try { await axios.post("/api/smartpay/employees", newEmployee); toast({ title: "Employee created" }); setShowNewEmployee(false); fetchEmployees(); } catch { toast({ title: "Failed", variant: "destructive" }); }
  };

  const handleTerminate = async (id) => {
    try { await axios.post(`/api/smartpay/employees/${id}/terminate`); toast({ title: "Employee terminated" }); fetchEmployees(); } catch { toast({ title: "Failed", variant: "destructive" }); }
  };

  const handleCreateWorkflow = async () => {
    try {
      await axios.post("/api/smartpay/onboarding-workflows", {
        ...newWorkflow,
        steps: newWorkflow.steps.split("\n").filter(Boolean).map(s => ({ name: s.trim(), description: s.trim() }))
      });
      toast({ title: "Workflow created" }); setShowNewWorkflow(false); fetchWorkflows();
    } catch { toast({ title: "Failed", variant: "destructive" }); }
  };

  const handleStartOnboarding = async () => {
    try { await axios.post("/api/smartpay/onboarding/start", { employee_id: onboardEmpId, workflow_id: onboardWfId }); toast({ title: "Onboarding started" }); setShowStartOnboarding(false); fetchOnboarding(); } catch { toast({ title: "Failed", variant: "destructive" }); }
  };

  const handleAdvanceOnboarding = async (id) => {
    try { await axios.post(`/api/smartpay/onboarding/${id}/advance`); toast({ title: "Step advanced" }); fetchOnboarding(); } catch { toast({ title: "Failed", variant: "destructive" }); }
  };

  const handleCreateOffboarding = async () => {
    try {
      await axios.post("/api/smartpay/offboarding", {
        ...newOffboarding,
        checklist: newOffboarding.checklist.split("\n").filter(Boolean).map(t => ({ task: t.trim(), completed: false }))
      });
      toast({ title: "Offboarding created" }); setShowNewOffboarding(false); fetchOffboardings();
    } catch { toast({ title: "Failed", variant: "destructive" }); }
  };

  const handleCompleteTask = async (id, taskIdx) => {
    try { await axios.post(`/api/smartpay/offboarding/${id}/complete-task`, { task_index: taskIdx }); toast({ title: "Task completed" }); fetchOffboardings(); } catch { toast({ title: "Failed", variant: "destructive" }); }
  };

  const handleCompleteOffboarding = async (id) => {
    try { await axios.post(`/api/smartpay/offboarding/${id}/complete`); toast({ title: "Offboarding completed" }); fetchOffboardings(); } catch { toast({ title: "Failed", variant: "destructive" }); }
  };

  const handleUploadDoc = async () => {
    try { await axios.post(`/api/smartpay/employees/${newDoc.employee_id}/documents`, newDoc); toast({ title: "Document uploaded" }); setShowUploadDoc(false); fetchDocuments(newDoc.employee_id); } catch { toast({ title: "Failed", variant: "destructive" }); }
  };

  const handleSignDoc = async (id) => {
    try { await axios.post(`/api/smartpay/documents/${id}/sign`); toast({ title: "Document signed" }); fetchDocuments(selectedEmpId); } catch { toast({ title: "Failed", variant: "destructive" }); }
  };

  const handleClockIn = async () => {
    try { await axios.post("/api/smartpay/time/clock-in", { employee_id: selectedEmpId }); toast({ title: "Clocked in" }); setClockedIn(true); fetchTimeEntries(selectedEmpId); } catch { toast({ title: "Failed", variant: "destructive" }); }
  };

  const handleClockOut = async () => {
    try { await axios.post("/api/smartpay/time/clock-out", { employee_id: selectedEmpId }); toast({ title: "Clocked out" }); setClockedIn(false); fetchTimeEntries(selectedEmpId); } catch { toast({ title: "Failed", variant: "destructive" }); }
  };

  const empMap = Object.fromEntries(employees.map(e => [e.id, e.full_name]));

  return (
    <div className="space-y-6">
      <div><h1 className="text-2xl font-bold">HRIS Core</h1><p className="text-muted-foreground">Employees, onboarding, offboarding, documents & time tracking</p></div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="employees">Employees</TabsTrigger>
          <TabsTrigger value="onboarding">Onboarding</TabsTrigger>
          <TabsTrigger value="offboarding">Offboarding</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="time">Time & Attendance</TabsTrigger>
        </TabsList>

        <TabsContent value="employees" className="mt-4">
          <div className="flex justify-between mb-4">
            <h2 className="text-lg font-semibold">Employees</h2>
            <Dialog open={showNewEmployee} onOpenChange={setShowNewEmployee}>
              <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-1" /> New Employee</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>New Employee</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Full Name</Label><Input value={newEmployee.full_name} onChange={e => setNewEmployee(p => ({ ...p, full_name: e.target.value }))} /></div>
                  <div><Label>Email</Label><Input value={newEmployee.email} onChange={e => setNewEmployee(p => ({ ...p, email: e.target.value }))} /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label>Phone</Label><Input value={newEmployee.phone} onChange={e => setNewEmployee(p => ({ ...p, phone: e.target.value }))} /></div>
                    <div><Label>Department</Label><Input value={newEmployee.department} onChange={e => setNewEmployee(p => ({ ...p, department: e.target.value }))} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label>Position</Label><Input value={newEmployee.position} onChange={e => setNewEmployee(p => ({ ...p, position: e.target.value }))} /></div>
                    <div><Label>Type</Label><Select value={newEmployee.employee_type} onValueChange={v => setNewEmployee(p => ({ ...p, employee_type: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="FULL_TIME">Full Time</SelectItem><SelectItem value="PART_TIME">Part Time</SelectItem><SelectItem value="CONTRACT">Contract</SelectItem><SelectItem value="INTERN">Intern</SelectItem></SelectContent>
                    </Select></div>
                  </div>
                  <div><Label>Start Date</Label><Input type="date" value={newEmployee.start_date} onChange={e => setNewEmployee(p => ({ ...p, start_date: e.target.value }))} /></div>
                  <Button onClick={handleCreateEmployee} className="w-full">Create</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <div className="rounded-md border">
            <Table>
              <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Department</TableHead><TableHead>Position</TableHead><TableHead>Type</TableHead><TableHead>Status</TableHead><TableHead>Action</TableHead></TableRow></TableHeader>
              <TableBody>
                {employees.map(e => (
                  <TableRow key={e.id}>
                    <TableCell className="font-medium">{e.full_name}</TableCell>
                    <TableCell>{e.email}</TableCell>
                    <TableCell>{e.department}</TableCell>
                    <TableCell>{e.position}</TableCell>
                    <TableCell><Badge variant="outline">{e.employee_type}</Badge></TableCell>
                    <TableCell><Badge variant={e.status === "ACTIVE" ? "default" : "destructive"}>{e.status}</Badge></TableCell>
                    <TableCell>
                      {e.status === "ACTIVE" && (
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleTerminate(e.id)}><XCircle className="h-4 w-4 mr-1" /> Terminate</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {employees.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No employees</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="onboarding" className="mt-4">
          <div className="flex justify-between mb-4">
            <h2 className="text-lg font-semibold">Onboarding</h2>
            <div className="flex gap-2">
              <Dialog open={showNewWorkflow} onOpenChange={setShowNewWorkflow}>
                <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-1" /> New Workflow</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>New Onboarding Workflow</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div><Label>Name</Label><Input value={newWorkflow.name} onChange={e => setNewWorkflow(p => ({ ...p, name: e.target.value }))} /></div>
                    <div><Label>Description</Label><Textarea value={newWorkflow.description} onChange={e => setNewWorkflow(p => ({ ...p, description: e.target.value }))} /></div>
                    <div><Label>Steps (one per line)</Label><Textarea value={newWorkflow.steps} onChange={e => setNewWorkflow(p => ({ ...p, steps: e.target.value }))} placeholder="Submit documents&#10;IT setup&#10;Orientation&#10;Benefits enrollment" /></div>
                    <Button onClick={handleCreateWorkflow} className="w-full">Create</Button>
                  </div>
                </DialogContent>
              </Dialog>
              <Dialog open={showStartOnboarding} onOpenChange={setShowStartOnboarding}>
                <DialogTrigger asChild><Button size="sm"><Play className="h-4 w-4 mr-1" /> Start Onboarding</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Start Onboarding</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div><Label>Employee</Label><Select value={onboardEmpId} onValueChange={setOnboardEmpId}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{employees.filter(e => e.status === "ACTIVE").map(e => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}</SelectContent>
                    </Select></div>
                    <div><Label>Workflow</Label><Select value={onboardWfId} onValueChange={setOnboardWfId}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{onboardingWorkflows.map(w => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
                    </Select></div>
                    <Button onClick={handleStartOnboarding} className="w-full">Start</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
          <div className="space-y-4">
            <div className="rounded-md border">
              <Table>
                <TableHeader><TableRow><TableHead>Employee</TableHead><TableHead>Workflow</TableHead><TableHead>Current Step</TableHead><TableHead>Progress</TableHead><TableHead>Status</TableHead><TableHead>Action</TableHead></TableRow></TableHeader>
                <TableBody>
                  {onboardingInstances.map(o => (
                    <TableRow key={o.id}>
                      <TableCell>{empMap[o.employee_id] || o.employee_id?.slice(0, 12)}</TableCell>
                      <TableCell>{o.workflow_name || o.workflow_id?.slice(0, 12)}</TableCell>
                      <TableCell>{o.current_step_name || o.current_step || "-"}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-24 bg-slate-200 rounded-full h-2"><div className="bg-teal-500 h-2 rounded-full" style={{ width: `${o.progress_percentage || 0}%` }} /></div>
                          <span className="text-xs">{o.progress_percentage || 0}%</span>
                        </div>
                      </TableCell>
                      <TableCell><Badge variant={o.status === "COMPLETED" ? "default" : "warning"}>{o.status}</Badge></TableCell>
                      <TableCell>
                        {o.status !== "COMPLETED" && (
                          <Button size="sm" variant="outline" onClick={() => handleAdvanceOnboarding(o.id)}>
                            <Play className="h-4 w-4 mr-1" /> Advance
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {onboardingInstances.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No active onboarding</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="offboarding" className="mt-4">
          <div className="flex justify-between mb-4">
            <h2 className="text-lg font-semibold">Offboarding</h2>
            <Dialog open={showNewOffboarding} onOpenChange={setShowNewOffboarding}>
              <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-1" /> New Offboarding</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>New Offboarding</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Employee</Label><Select value={newOffboarding.employee_id} onValueChange={v => setNewOffboarding(p => ({ ...p, employee_id: v }))}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}</SelectContent>
                  </Select></div>
                  <div><Label>Reason</Label><Textarea value={newOffboarding.reason} onChange={e => setNewOffboarding(p => ({ ...p, reason: e.target.value }))} /></div>
                  <div><Label>Checklist (one task per line)</Label><Textarea value={newOffboarding.checklist} onChange={e => setNewOffboarding(p => ({ ...p, checklist: e.target.value }))} placeholder="Collect laptop&#10;Return badge&#10;Exit interview&#10;Final paycheck" /></div>
                  <Button onClick={handleCreateOffboarding} className="w-full">Create</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <div className="space-y-4">
            {offboardings.map(o => (
              <Card key={o.id}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="font-medium">{empMap[o.employee_id] || o.employee_id?.slice(0, 12)}</p>
                      <p className="text-sm text-muted-foreground">{o.reason}</p>
                    </div>
                    <Badge variant={o.status === "COMPLETED" ? "default" : "warning"}>{o.status}</Badge>
                  </div>
                  {(o.checklist || []).length > 0 && (
                    <div className="space-y-2">
                      {o.checklist.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between py-1 border-b last:border-0">
                          <div className="flex items-center gap-2">
                            {item.completed ? <CheckCircle className="h-4 w-4 text-green-500" /> : <div className="h-4 w-4 border rounded" />}
                            <span className={item.completed ? "line-through text-muted-foreground" : ""}>{item.task}</span>
                          </div>
                          {!item.completed && (
                            <Button size="sm" variant="ghost" onClick={() => handleCompleteTask(o.id, idx)}><CheckCircle className="h-4 w-4" /></Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {o.status !== "COMPLETED" && (
                    <Button size="sm" className="mt-3" onClick={() => handleCompleteOffboarding(o.id)}>Complete Offboarding</Button>
                  )}
                </CardContent>
              </Card>
            ))}
            {offboardings.length === 0 && <p className="text-center py-8 text-muted-foreground">No offboarding processes</p>}
          </div>
        </TabsContent>

        <TabsContent value="documents" className="mt-4">
          <div className="flex items-end gap-4 mb-4">
            <div className="flex-1">
              <Label>Select Employee</Label>
              <Select value={selectedEmpId} onValueChange={v => { setSelectedEmpId(v); fetchDocuments(v); }}>
                <SelectTrigger><SelectValue placeholder="Choose employee" /></SelectTrigger>
                <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {selectedEmpId && (
              <Dialog open={showUploadDoc} onOpenChange={setShowUploadDoc}>
                <DialogTrigger asChild><Button size="sm"><Upload className="h-4 w-4 mr-1" /> Upload Document</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Upload Document</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div><Label>Title</Label><Input value={newDoc.title} onChange={e => setNewDoc(p => ({ ...p, title: e.target.value }))} /></div>
                    <div><Label>Type</Label><Select value={newDoc.type} onValueChange={v => setNewDoc(p => ({ ...p, type: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="CONTRACT">Contract</SelectItem><SelectItem value="NDA">NDA</SelectItem><SelectItem value="POLICY">Policy</SelectItem><SelectItem value="ONBOARDING">Onboarding</SelectItem><SelectItem value="OTHER">Other</SelectItem></SelectContent>
                    </Select></div>
                    <div><Label>File URL</Label><Input value={newDoc.file_url} onChange={e => setNewDoc(p => ({ ...p, file_url: e.target.value }))} placeholder="https://..." /></div>
                    <Button onClick={handleUploadDoc} className="w-full">Upload</Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
          <div className="rounded-md border">
            <Table>
              <TableHeader><TableRow><TableHead>Title</TableHead><TableHead>Type</TableHead><TableHead>Signed</TableHead><TableHead>Date</TableHead><TableHead>Action</TableHead></TableRow></TableHeader>
              <TableBody>
                {documents.map(d => (
                  <TableRow key={d.id}>
                    <TableCell className="font-medium">{d.title}</TableCell>
                    <TableCell><Badge variant="outline">{d.type}</Badge></TableCell>
                    <TableCell>{d.signed ? <CheckCircle className="h-4 w-4 text-green-500" /> : <XCircle className="h-4 w-4 text-amber-500" />}</TableCell>
                    <TableCell className="text-sm">{d.uploaded_at || "-"}</TableCell>
                    <TableCell>
                      {!d.signed && (
                        <Button size="sm" variant="outline" onClick={() => handleSignDoc(d.id)}>
                          <Signature className="h-4 w-4 mr-1" /> Sign
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {documents.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Select an employee to view documents</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="time" className="mt-4">
          <div className="flex items-end gap-4 mb-4">
            <div className="flex-1">
              <Label>Select Employee</Label>
              <Select value={selectedEmpId} onValueChange={v => { setSelectedEmpId(v); fetchTimeEntries(v); setClockedIn(false); }}>
                <SelectTrigger><SelectValue placeholder="Choose employee" /></SelectTrigger>
                <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {selectedEmpId && (
              <Button size="sm" variant={clockedIn ? "destructive" : "default"} onClick={clockedIn ? handleClockOut : handleClockIn}>
                {clockedIn ? <><LogOut className="h-4 w-4 mr-1" /> Clock Out</> : <><LogIn className="h-4 w-4 mr-1" /> Clock In</>}
              </Button>
            )}
          </div>
          <div className="rounded-md border">
            <Table>
              <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Clock In</TableHead><TableHead>Clock Out</TableHead><TableHead>Hours</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>
                {timeEntries.map(t => (
                  <TableRow key={t.id}>
                    <TableCell>{t.date || t.clock_in?.slice(0, 10)}</TableCell>
                    <TableCell className="text-sm">{t.clock_in ? new Date(t.clock_in).toLocaleTimeString() : "-"}</TableCell>
                    <TableCell className="text-sm">{t.clock_out ? new Date(t.clock_out).toLocaleTimeString() : "-"}</TableCell>
                    <TableCell>{t.hours_worked ? t.hours_worked.toFixed(1) : "-"}</TableCell>
                    <TableCell><Badge variant={t.status === "COMPLETED" ? "default" : "warning"}>{t.status || "ACTIVE"}</Badge></TableCell>
                  </TableRow>
                ))}
                {timeEntries.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Select an employee to view time entries</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
